import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@folio/components/ui/table';
import { cn, formatDate, formatDateWithTime } from '@folio/lib';
import type { DocumentCoherenceDto, DocumentCoherenceFieldDto } from 'korint-sdk';
import type { DocumentCoherenceEntity } from '../../document-viewer/types';
import { AlertTriangleIcon, CheckIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const formatSubscriptionValue = ({ fieldType, subscriptionValue }: DocumentCoherenceFieldDto) => {
  if (!subscriptionValue) return subscriptionValue;
  if (fieldType === 'DATE') return formatDate(new Date(subscriptionValue));
  if (fieldType === 'DATETIME') return formatDateWithTime(new Date(subscriptionValue));
  return subscriptionValue;
};

export const countCoherenceMismatches = (coherence: DocumentCoherenceDto) =>
  coherence.fields.filter(({ result }) => result === 'MISMATCH').length;

function DocumentValue({ field }: { field: DocumentCoherenceFieldDto }) {
  const { t } = useTranslation();

  if (field.result === 'ABSENT') {
    return (
      <span className="text-muted-foreground">
        {field.documentValue ?? t('documents.coherence.absent')}
      </span>
    );
  }

  const isMatch = field.result === 'MATCH';
  const Icon = isMatch ? CheckIcon : AlertTriangleIcon;
  return (
    <span
      className={cn(
        'inline-flex items-start gap-1.5 font-medium [overflow-wrap:anywhere]',
        isMatch ? 'text-emerald-700' : 'text-amber-800',
      )}
    >
      <Icon
        className="mt-0.5 h-4 w-4 shrink-0"
        aria-label={t(isMatch ? 'documents.coherence.match' : 'documents.coherence.mismatch')}
      />
      {field.documentValue}
    </span>
  );
}

export function DocumentCoherenceTable({
  coherence,
  entity,
  productId,
}: {
  coherence: DocumentCoherenceDto;
  entity: DocumentCoherenceEntity;
  productId?: string;
}) {
  const { t } = useTranslation();

  if (coherence.status === 'PENDING') {
    return <p className="text-sm text-muted-foreground">{t('documents.coherence.pending')}</p>;
  }

  return (
    <div className="space-y-3">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t('documents.coherence.field')}</TableHead>
            <TableHead>{t(`documents.coherence.${entity}.subscription`)}</TableHead>
            <TableHead>{t('documents.coherence.document')}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {coherence.fields.map(field => (
            <TableRow
              key={`${field.entityType}.${field.fieldKey}`}
              className={cn(field.result === 'MISMATCH' && 'bg-amber-50 hover:bg-amber-50')}
            >
              <TableCell className="text-muted-foreground">
                {t([
                  `${productId}.fields.${field.fieldKey}.label`,
                  `fields.${field.fieldKey}.label`,
                  field.fieldKey,
                ])}
              </TableCell>
              <TableCell className="[overflow-wrap:anywhere]">
                {formatSubscriptionValue(field) ?? '—'}
              </TableCell>
              <TableCell>
                <DocumentValue field={field} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <p className="text-xs text-muted-foreground">{t('documents.coherence.footnote')}</p>
    </div>
  );
}
